import {
  ArrowUpRight,
  FileSearch,
  Plug,
  BatteryCharging,
  ScrollText,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { MARCHI, marchioAcceso, utenteInterno } from "@/lib/marchi";

/**
 * I tre esempi che vede chi apre l'app per la prima volta.
 *
 * SI VEDONO SOLO LA PRIMA VOLTA: chi ha gia' chiesto qualcosa trova le sue
 * ultime domande (UltimeDomande). Sono tre e non sette, perche' una parete di
 * proposte non invita, affoga (Davide, 26/08).
 *
 * DUE COSE IMPARATE LO STESSO GIORNO, E COSTANO CARO PROPRIO QUI
 *
 * 1. GLI ESEMPI SEGUONO L'ABBONAMENTO. Erano fissi: a chi aveva comprato solo
 *    AJAX, la primissima schermata proponeva "Schema cablaggio centrale INIM
 *    SmartLiving 1050L" - e cliccandolo si prendeva "INIM non e' incluso nel
 *    tuo abbonamento". Primo schermo, primo rifiuto, e per colpa nostra.
 *
 * 2. UN ESEMPIO SENZA SOGGETTO INSEGNA A CHIEDERE MALE. C'era "Errore E04
 *    sulla centrale, cosa significa?": quale centrale? Il motore adesso
 *    risponde chiedendo il modello - giustamente - ma un esempio che si prende
 *    una domanda in faccia e' un pessimo biglietto da visita. Gli esempi sono
 *    domande che sappiamo di saper rispondere: sono le stesse che stanno nei
 *    banchi di misura.
 */
type Esempio = { text: string; Icon: LucideIcon; marchio: string };

const ESEMPI: Esempio[] = [
  { text: "Che combinatore si monta sulla centrale INIM 10100L?", Icon: Plug, marchio: "INIM" },
  { text: "Che grado IP ha il MotionProtect Jeweller?", Icon: FileSearch, marchio: "AJAX" },
  { text: "Quanti rivelatori gestisce la centrale DAITEM SH380AT?", Icon: FileSearch, marchio: "DAITEM" },
  { text: "Quanti loop ha una centrale NOTIFIER AM2000?", Icon: Plug, marchio: "NOTIFIER" },
  // Trasversali: valgono per chiunque, qualunque marchio abbia comprato.
  { text: "Quali norme cito nella dichiarazione di conformità di un impianto antintrusione?", Icon: ScrollText, marchio: "*" },
  { text: "Come dimensiono la batteria tampone per 24 ore di autonomia?", Icon: BatteryCharging, marchio: "*" },
];

export function QuickPrompts({ onPick }: { onPick: (p: string) => void }) {
  const { profile, user } = useAuth();
  const interno = utenteInterno(user?.email);
  const suoi = (profile?.brands ?? []).map((b) => b.toUpperCase());

  // Senza marchi dichiarati siamo in prova, e in prova ci sono tutti: si
  // mostrano i marchi piu' diffusi piu' una domanda trasversale.
  const ammessi = suoi.length > 0 ? suoi : [...MARCHI];
  // Un marchio spento non si propone nemmeno per sbaglio: il profilo potrebbe
  // portarselo dietro da un abbonamento venduto prima che lo spegnessimo.
  const dimarchio = ESEMPI
    .filter((e) => ammessi.includes(e.marchio) && (marchioAcceso(e.marchio) || interno))
    .slice(0, 2);
  const trasversali = ESEMPI.filter((e) => e.marchio === "*");
  const scelti = [...dimarchio, ...trasversali].slice(0, 3);

  if (scelti.length === 0) return null;

  return (
    <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {scelti.map(({ text, Icon }) => (
        <button
          key={text}
          onClick={() => onPick(text)}
          className="group flex items-start justify-between gap-3 rounded-xl border border-border/40 bg-surface/60 p-5 text-left text-foreground/90 transition-all duration-150 ease-out hover:-translate-y-px hover:border-primary/60 hover:bg-surface hover:shadow-glow"
        >
          <span className="flex min-w-0 items-start gap-3">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium leading-snug">{text}</span>
          </span>
          <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
        </button>
      ))}
    </div>
  );
}
